import _ from 'lodash';
import React, { forwardRef, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { FiChevronDown, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { CircleSpinner } from 'react-spinners-kit';
import styled from 'styled-components';
import { AlarmClockSvg } from '../../../assets/svg-icon-components/AlarmClockSvg';
import { CalendarSvg } from '../../../assets/svg-icon-components/CalendarSvg';
import { ClockSvg } from '../../../assets/svg-icon-components/ClockSvg';
import { UsersSvg } from '../../../assets/svg-icon-components/UsersSvg';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { HorizontalContent } from '../../core/HorizontalContent';
import { IntroductionVideoCard } from '../../core/reusable-forms/IntroductionVideoCard';
import { PublishingStatusCard } from '../../core/reusable-forms/PublishingStatusCard';
import { TagsCard } from '../../core/reusable-forms/TagsCard';
import { TitleAndDescriptionCard } from '../../core/reusable-forms/TitleAndDescriptionCard';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhCardListItem } from '../../core/RhCardListItem';
import { RhFormGrid, RhFormGridColumn } from '../../core/RhFormGrid';
import { RhImage } from '../../core/RhImage';
import { RhInput, RhInputMode } from '../../core/RhInput';
import { RhModal } from '../../core/RhModal';
import { RhSelect, RhSelectOption } from '../../core/RhSelect';
import { SizedBox } from '../../core/SizedBox';
import { colors, H1, H2 } from '../../core/Styles';
import { EventFormProps } from '../containers/EventFormContainer';

/** Custom date input component for react-datepicker */
const CustomDateInput = forwardRef((props: any, ref: any) => {
  return (
    <div ref={ref}>
      <RhButton
        disabled={props.disabled}
        mode={RhButtonMode.subtle}
        suffixIcon={FiChevronDown}
        onClick={props.onClick}
        title={props.value || 'Select'}
      />
    </div>
  );
});

export const EventFormScreen: React.FC<EventFormProps> = props => {
  // { [leaderId]: { label, value } }
  const eventLeaderLib = useMemo(() => {
    const lib: { [key: string]: RhSelectOption } = {};
    props.eventLeaders.forEach(l => {
      lib[l.value] = l;
    });
    return lib;
  }, [props.eventLeaders]);

  return (
    <DashboardWrapper>
      <DashboardHeader
        title="New event"
        actions={[
          <RhButton
            key="event-form--cancel"
            title="Cancel"
            mode={RhButtonMode.text}
            onClick={props.userClickedCancel}
          />,
          <RhButton
            key="event-form--save"
            title="Save Changes"
            disabled={props.isSaving || props.isLoadingPrograms}
            loading={props.isSaving || props.isLoadingPrograms}
            onClick={props.userClickedSave}
          />,
        ]}
      />
      <RhFormGrid>
        <RhFormGridColumn>
          {/* Title and Description  */}
          <TitleAndDescriptionCard
            form={{
              title: props.form.title || '',
              description: props.form.description,
            }}
            isEditingTitle={props.isEditingTitle}
            userClickedEditTitle={props.userClickedEditTitle}
            userUpdatedForm={formState =>
              props.userUpdatedForm({
                ...props.form,
                ...formState,
              })
            }
          />
          <SchedulingGrid>
            <RhCard>
              <H2>Scheduled date</H2>
              <SizedBox height={12} />
              <DateInputDiv>
                <CalendarSvg width={20} height={20} />
                <DatePicker
                  showPopperArrow={false}
                  selected={props.form.scheduledDate}
                  onChange={(date: Date) =>
                    props.userUpdatedForm({
                      ...props.form,
                      scheduledDate: date,
                    })
                  }
                  customInput={<CustomDateInput />}
                  dateFormat="MMMM dd, yyyy, EEE"
                  minDate={new Date()}
                />
              </DateInputDiv>
            </RhCard>
            <RhCard>
              <H2>Start Time</H2>
              <SizedBox height={12} />
              <DateInputDiv>
                <ClockSvg width={20} height={20} />
                <DatePicker
                  showPopperArrow={false}
                  selected={props.form.startTime}
                  onChange={(date: Date) =>
                    props.userUpdatedForm({
                      ...props.form,
                      startTime: date,
                    })
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  customInput={<CustomDateInput />}
                  dateFormat="hh:mm aa"
                  timeIntervals={15}
                />
              </DateInputDiv>
            </RhCard>
            <RhCard>
              <H2>End Time</H2>
              <SizedBox height={12} />
              <DateInputDiv>
                <AlarmClockSvg width={20} height={20} />
                <DatePicker
                  showPopperArrow={false}
                  disabled={!props.form.startTime}
                  selected={props.form.endTime}
                  onChange={(date: Date) =>
                    props.userUpdatedForm({
                      ...props.form,
                      endTime: date,
                    })
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  customInput={<CustomDateInput />}
                  dateFormat="hh:mm aa"
                  timeIntervals={15}
                  filterTime={time => {
                    // filter startTime and less
                    const selectedDate = new Date(time);
                    const startDate = props.form.startTime;
                    return startDate!.getTime() < selectedDate.getTime();
                  }}
                />
              </DateInputDiv>
            </RhCard>
            <RhCard>
              <H2>People Registered</H2>
              <SizedBox height={12} />
              <DateInputDiv>
                <UsersSvg width={20} height={20} />
                <H1 color={colors.primary}>0</H1>
              </DateInputDiv>
            </RhCard>
          </SchedulingGrid>
          <RhCard>
            <H2>Study Program</H2>
            <SizedBox height={20} />
            {!!props.form.program ? (
              <RhCardListItem
                imageMode="minirect"
                imageUrl={props.form.program?.imageUrl || ''}
                title={props.form.program?.title}
                author={_.startCase(props.form.program?.authorName) || '-'}
                authorImage={props.form.program?.authorImage}
                dateUpdated={props.form.program?.dateUpdated}
                description={props.form.program?.description}
                onClick={props.userToggledProgramsModal}
                topRightActions={
                  <RhButton
                    mode={RhButtonMode.subtle}
                    prefixIcon={FiTrash2}
                    onClick={() =>
                      props.userUpdatedForm({
                        ...props.form,
                        program: null,
                      })
                    }
                  />
                }
              />
            ) : (
              <RhButton
                mode={RhButtonMode.subtle}
                prefixIcon={FiPlus}
                title="Select Program"
                onClick={props.userToggledProgramsModal}
                disabled={props.isLoadingPrograms}
                loading={props.isLoadingPrograms}
              />
            )}
          </RhCard>
          <RhCard>
            <div className="flex items-center justify-between">
              <H2>Vimeo Event URL</H2>
              <div className="flex items-center space-x-1 text-blue-600 font-bold hover:underline">
                <IoInformationCircleOutline />
                <a
                  rel="noreferrer noopener"
                  target="_blank"
                  href="https://scribehow.com/shared/Risen_Hope_Portal_Where_do_I_get_the_Vimeo_Event_URL__Jly1v-oZQDmXZGq_9WippA">
                  Where can I get this?
                </a>
              </div>
            </div>
            <SizedBox height={12} />
            <RhInput
              mode={RhInputMode.subtle}
              placeholder="e.g. https://vimeo.com/event/1234567/12ab34cd56"
              containerStyle={{ width: '100%' }}
              value={props.form.vimeoEventUrl}
              onChange={e =>
                props.userUpdatedForm({
                  ...props.form,
                  vimeoEventUrl: e.target.value,
                })
              }
            />
          </RhCard>
        </RhFormGridColumn>
        <RhFormGridColumn>
          {/* Publishing Status */}
          <PublishingStatusCard
            showDescription={false}
            publishingStatusValue={props.form.publishingStatus || null}
            onPublishingStatusChange={selectedOption =>
              props.userUpdatedForm({
                ...props.form,
                publishingStatus: selectedOption.value,
              })
            }
          />
          {/* Event Leader */}
          <RhCard mode="tiled">
            <H2>Event Leader</H2>
            <SizedBox height={12} />
            <RhSelect
              value={eventLeaderLib[props.form.leaderId]}
              isLoading={props.isLoadingEventLeaders}
              isDisabled={props.isLoadingEventLeaders}
              isSearchable={true}
              options={props.eventLeaders}
              onChange={option =>
                props.userUpdatedForm({
                  ...props.form,
                  leaderId: option.value,
                })
              }
            />
          </RhCard>
          {/* Feature Image */}
          <RhCard mode="tiled">
            <H2>Feature Image</H2>
            {!!props.form.program?.imageUrl && (
              <>
                <SizedBox height={12} />
                <RhImage mode="full" src={props.form.program.imageUrl} />
              </>
            )}
          </RhCard>
          {/* Introduction Video */}
          <IntroductionVideoCard
            form={{ introVideo: props.form.introVideo }}
            userClickedDelete={() =>
              props.userUpdatedForm({
                ...props.form,
                introVideo: undefined,
              })
            }
            userUpdatedForm={formState =>
              props.userUpdatedForm({
                ...props.form,
                ...formState,
              })
            }
          />
          {/* Tags */}
          <TagsCard
            form={props.form}
            userPushedToTags={props.userPushedToTags}
            userUpdatedForm={formState =>
              props.userUpdatedForm({
                ...props.form,
                ...formState,
              })
            }
          />
          {/* Taxonomic Category */}
          {/* TODO: Add taxonomic category */}
          {/* <TaxonomicCategoryCard /> */}
        </RhFormGridColumn>
      </RhFormGrid>

      {/* Study Programs Modal */}
      <RhModal
        contentStyle={{ width: '75%', maxHeight: '90%' }}
        isOpen={props.isProgramsModalOpen}
        onRequestClose={props.userToggledProgramsModal}>
        <HorizontalContent
          left={[
            <H2 key="title">Study Programs</H2>,
            props.isLoadingPrograms && <CircleSpinner key="loader" size={15} color={colors.primary} />,
          ]}
          right={[
            <RhButton key="cancel" mode={RhButtonMode.text} title="Cancel" onClick={props.userToggledProgramsModal} />,
            <RhInput key="search" icon={FiSearch} placeholder="Search" />,
          ]}
        />
        <SizedBox height={15} />
        <ProgramGrid>
          {props.bibleStudies.map(study => (
            <RhCardListItem
              key={study.id}
              onClick={() => props.userSelectedProgram(study)}
              imageUrl={study.imageUrl}
              title={study.title}
              author={_.startCase(study.authorName) || '-'}
              dateUpdated={study.dateUpdated}
              authorImage={study.authorImage}
            />
          ))}
        </ProgramGrid>
      </RhModal>
    </DashboardWrapper>
  );
};

const SchedulingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: 20px;

  .react-datepicker {
    font-family: 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
      'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    background: #ffffff;
    box-shadow: 0px 0px 5px 1px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    border: none;
  }
  .react-datepicker__header {
    background: #ffffff;
    border-bottom: none;
  }
  .react-datepicker__current-month {
    font-weight: bold;
    font-size: 16px;
    letter-spacing: 0.035em;
    margin-bottom: 8px;
  }
  .react-datepicker__day-names {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 10px;
    letter-spacing: 0.035em;
  }
  .react-datepicker__day--outside-month {
    color: rgba(0, 0, 0, 0.5);
  }
  .react-datepicker__day--selected {
    background: ${colors.primary};
  }
  .react-datepicker__time-list {
    outline: none;
  }
  .react-datepicker__time-list-item--selected {
    background-color: ${colors.primary} !important;
  }
`;

const DateInputDiv = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 16px;
  }
`;

const ProgramGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
`;
